// src/features/admin/components/settings/system-preferences/components/RegionalSettings.tsx
import React from 'react';
import { BriefcaseIcon, CurrencyDollarIcon, GlobeAltIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { usePreferences } from '../PreferencesContext';
import type { OperatingContext, ServiceArea } from '../../../../services/SystemPreferencesAPI';

const TIME_ZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (No DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Berlin', label: 'Central Europe' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time' },
];

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)', example: '12/31/2025' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (International)', example: '31/12/2025' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)', example: '2025-12-31' },
];

const TIME_FORMATS = [
  { value: '12h', label: '12-hour (AM/PM)', example: '3:30 PM' },
  { value: '24h', label: '24-hour', example: '15:30' },
];

const CURRENCIES = [
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'British Pound (£)' },
  { value: 'CAD', label: 'Canadian Dollar (C$)' },
];

const DEFAULT_OPERATING_CONTEXTS: OperatingContext[] = [
  { type: 'region', code: 'US', label: 'United States', enabled: true, extension: 'base' },
  { type: 'business_function', code: 'general_operations', label: 'General Operations', enabled: true, extension: 'base' },
  { type: 'regulation', code: 'SOC2', label: 'SOC 2', enabled: false, extension: 'base' },
  { type: 'regulation', code: 'GDPR', label: 'GDPR', enabled: false, extension: 'base' },
  { type: 'industry', code: 'legal', label: 'Legal Services', enabled: false, extension: 'legal' },
  { type: 'industry', code: 'healthcare', label: 'Healthcare Operations', enabled: false, extension: 'healthcare' },
  { type: 'industry', code: 'construction', label: 'Construction Operations', enabled: false, extension: 'construction' },
];

const DEFAULT_SERVICE_AREAS: ServiceArea[] = [
  { code: 'general_operations', label: 'General Operations', category: 'operations', enabled: true, extension: 'base' },
  { code: 'client_onboarding', label: 'Client Onboarding', category: 'customer', enabled: false, extension: 'base' },
  { code: 'document_review', label: 'Document Review', category: 'documents', enabled: false, extension: 'base' },
  { code: 'billing_operations', label: 'Billing Operations', category: 'finance', enabled: false, extension: 'base' },
  { code: 'support_operations', label: 'Support Operations', category: 'customer', enabled: false, extension: 'base' },
  { code: 'family_law', label: 'Family Law', category: 'legal', enabled: false, extension: 'legal' },
  { code: 'contract_management', label: 'Contract Management', category: 'legal', enabled: false, extension: 'legal' },
];

function mergeCatalog<T extends { code: string; enabled: boolean }>(catalog: T[], selected: T[] = []): T[] {
  const selectedByCode = new Map(selected.map((item) => [item.code, item]));
  const merged = catalog.map((item) => selectedByCode.get(item.code) || item);
  for (const item of selected) {
    if (!catalog.some((catalogItem) => catalogItem.code === item.code)) {
      merged.push(item);
    }
  }
  return merged;
}

const RegionalSettings: React.FC = () => {
  const { preferences, updatePreference } = usePreferences();

  if (!preferences) {
    return null;
  }

  const operatingContexts = mergeCatalog(DEFAULT_OPERATING_CONTEXTS, preferences.operating_contexts || []);
  const serviceAreas = mergeCatalog(DEFAULT_SERVICE_AREAS, preferences.service_areas || []);

  const updateOperatingContexts = (contexts: OperatingContext[]) => {
    updatePreference('operating_contexts', contexts);
    updatePreference(
      'business_regions',
      contexts
        .filter((context) => context.enabled && ['region', 'market'].includes(context.type))
        .map((context) => context.code),
    );
  };

  const toggleOperatingContext = (code: string) => {
    updateOperatingContexts(
      operatingContexts.map((context) =>
        context.code === code ? { ...context, enabled: !context.enabled } : context,
      ),
    );
  };

  const toggleServiceArea = (code: string) => {
    updatePreference(
      'service_areas',
      serviceAreas.map((area) =>
        area.code === code ? { ...area, enabled: !area.enabled } : area,
      ),
    );
  };

  return (
    <div className="regional-settings space-y-8">
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <GlobeAltIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Time Zone</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set the primary time zone for this subscriber workspace.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={preferences.timezone}
            onChange={(event) => updatePreference('timezone', event.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {TIME_ZONES.map((timezone) => (
              <option key={timezone.value} value={timezone.value}>{timezone.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <GlobeAltIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Date & Time Formats</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose how dates and times are displayed throughout the application.
            </p>
          </div>
        </div>

        <div className="ml-9 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Format</label>
            <select
              value={preferences.date_format}
              onChange={(event) => updatePreference('date_format', event.target.value)}
              className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {DATE_FORMATS.map((format) => (
                <option key={format.value} value={format.value}>
                  {format.label} - Example: {format.example}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time Format</label>
            <select
              value={preferences.time_format}
              onChange={(event) => updatePreference('time_format', event.target.value)}
              className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              {TIME_FORMATS.map((format) => (
                <option key={format.value} value={format.value}>
                  {format.label} - Example: {format.example}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <CurrencyDollarIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Default Currency</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose the primary currency for billing and financial displays.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={preferences.currency}
            onChange={(event) => updatePreference('currency', event.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {CURRENCIES.map((currency) => (
              <option key={currency.value} value={currency.value}>{currency.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <MapPinIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Operating Contexts</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Define the regions, regulations, industries, and business functions that should influence workspace defaults and future capability packs.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <div className="mb-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Enabled: {operatingContexts.filter((context) => context.enabled).length} context{operatingContexts.filter((context) => context.enabled).length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {operatingContexts.map((context) => (
                <label
                  key={context.code}
                  className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={context.enabled}
                    onChange={() => toggleOperatingContext(context.code)}
                    className="h-4 w-4 mt-0.5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
                  />
                  <span>
                    <span className="block text-sm text-gray-900 dark:text-gray-100">{context.label}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{context.type} · {context.extension || 'base'}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <BriefcaseIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">Service Areas</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select the kinds of work this subscriber performs. Industry extensions can later bind service areas and operating contexts to tasks, forms, prompts, automations, and workflows.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <div className="mb-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Enabled: {serviceAreas.filter((area) => area.enabled).length} service area{serviceAreas.filter((area) => area.enabled).length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {serviceAreas.map((area) => (
                <label
                  key={area.code}
                  className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={area.enabled}
                    onChange={() => toggleServiceArea(area.code)}
                    className="h-4 w-4 mt-0.5 text-lex-accent focus:ring-lex-accent border-gray-300 rounded"
                  />
                  <span>
                    <span className="block text-sm text-gray-900 dark:text-gray-100">{area.label}</span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{area.category || 'operations'} · {area.extension || 'base'}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionalSettings;
